import { appConfigStore } from 'Models/AppConfigStore';
import { isNotNullOrUndefined, isNullOrUndefined, stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { genId } from 'Util/_HumanWritten/IdUtils';
import { BookingAddOn } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BookingAddOn';
import { WizardErrors } from '../BookingWizardTypes';
import { FreightTypeDto } from 'Services/Api/_HumanWritten/BookingWizardDataService';

export class FreightBookingWizardData {
	type: 'freight';

	freightCategoryId: string;
	freightTypeId: string;
	quantity: number | null;
	weight: number | null;
	width: number | null;
	height: number | null;
	length: number | null;
	handlingIds: string[];
	description: string;

	receiverFirstName?: string;
	receiverLastName?: string;
	receiverPhone?: string;
	receiverEmail?: string;
	receiverCompanyName?: string;

	notifyFirstName?: string;
	notifyLastName?: string;
	notifyPhone?: string;
	notifyEmail?: string;
	notifyCompanyName?: string;

	// Admin-configured dynamic field values, keyed by DynamicBookingFieldEntity.Id (uuid).
	dynamicFieldValues?: Record<string, string>;

	// Additional options
	departingTripOptions: BookingAddOn[];
	returningTripOptions: BookingAddOn[];

	items: FreightBookingDetailState[] = [];

	constructor(data: Partial<FreightBookingWizardData>) {
		Object.assign(this, data);
		if (data.departingTripOptions) {
			this.departingTripOptions = data.departingTripOptions.map(x => new BookingAddOn(x));
		}
		if (data.returningTripOptions) {
			this.returningTripOptions = data.returningTripOptions.map(x => new BookingAddOn(x));
		}
	}
}

export class FreightBookingDetailState {
	freightCategoryId: string;
	freightTypeId: string;
	handlingIds: string[];
	quantity: number | null;
	weight: number | null;
	width: number | null;
	height: number | null;
	length: number | null;
	description: string;
	addOns: BookingAddOn[] = [];

	// UI related
	id: string;
	errors: WizardErrors<FreightBookingDetailState> = {};

	constructor(data: Partial<FreightBookingDetailState>) {
		Object.assign(this, data);
	}
}

export function getFreightBookingDetail(): FreightBookingDetailState {
	return new FreightBookingDetailState({
		id: genId(),
		freightTypeId: '',
		freightCategoryId: '',
		quantity: null,
		weight: null,
		width: null,
		height: null,
		length: null,
		handlingIds: [],
		description: '',
		addOns: [],
	});
}

/**
 * Same as FreightBookingDetailState and FreightBookingWizardData but only looking at the properties required for validation
 */
interface ValidateFreightDetailArgs {
	freightCategoryId: string;
	freightTypeId: string;
	handlingIds: string[];
	quantity: number | null;
	weight: number | null;
	width: number | null;
	height: number | null;
	length: number | null;
	description: string;
}

interface ValidateFreightDetailContext {
	freightTypeDtos: FreightTypeDto[];
	freightDimensionsHiddenOnStaticPriceEnabled: boolean;
}

export function validateFreightDetail(
	detail: ValidateFreightDetailArgs,
	context?: ValidateFreightDetailContext,
): WizardErrors<FreightBookingDetailState> {
	const newInnerErrors: WizardErrors<FreightBookingDetailState> = {};

	if (stringIsEmpty(detail.freightTypeId)) {
		newInnerErrors.freightTypeId = 'Required field';
	}

	if (stringIsEmpty(detail.freightCategoryId)) {
		newInnerErrors.freightCategoryId = 'Required field';
	}

	if (isNullOrUndefined(detail.quantity)) {
		newInnerErrors.quantity = 'Required field';
	} else if (detail?.quantity <= 0) {
		newInnerErrors.quantity = 'Must be greater than 0';
	}

	if (stringIsEmpty(detail.description)) {
		newInnerErrors.description = 'Required field';
	}

	if (!context) {
		validateWeightLengthHeight(detail, newInnerErrors);
	} else {
		const { freightTypeDtos } = context;

		const match = freightTypeDtos.find(f => f.id === detail.freightTypeId);
		if (match?.hasStaticPricing) {
			// THIS IS A VALIDATE FUNCTION,
			// we shouldnt really be assigning here,
			// but not sure where to put it at this moment...

			// Static-priced freight types don't use the dimensions for pricing, so no validation
			// is required. Instead, we assign the freight type's standard values / fallback to 1.
			detail.weight ??= match.minimumWeight ?? 1;
			detail.length ??= match.standardLength ?? 1;
			detail.height ??= match.standardHeight ?? 1;
			detail.width ??= match.standardWidth ?? 1;

			// Previously tried to assign value of 0, but repercussions appeared immediately e.g. freight details disappear
			// in the cart... many more problems could also occur, and it is not worth diving futher at this point in time
			// detail.weight ??= 0;
			// detail.length ??= 0;
			// detail.height ??= 0;
			// detail.width ??= 0;
		} else {
			// No freight type selected yet, or the freight type is NOT static-priced, so the
			// dimensions section is shown to the user (see showDimensions) and must be validated.
			validateWeightLengthHeight(detail, newInnerErrors);
		}
	}

	return newInnerErrors;
}

export function validateWeightLengthHeight(
	detail: Pick<ValidateFreightDetailArgs, 'weight' | 'length' | 'width' | 'height'>,
	newInnerErrors: WizardErrors<FreightBookingDetailState>,
) {
	if (isNullOrUndefined(detail.weight)) {
		newInnerErrors.weight = 'Required field';
	} else if (detail.weight <= 0) {
		newInnerErrors.weight = 'Must be greater than 0';
	}

	if (isNullOrUndefined(detail.length)) {
		newInnerErrors.length = 'Required field';
	} else if (detail.length <= 0) {
		newInnerErrors.length = 'Must be greater than 0';
	}

	if (isNullOrUndefined(detail.width)) {
		newInnerErrors.width = 'Required field';
	} else if (detail.width <= 0) {
		newInnerErrors.width = 'Must be greater than 0';
	}

	if (isNullOrUndefined(detail.height)) {
		newInnerErrors.height = 'Required field';
	} else if (detail.height <= 0) {
		newInnerErrors.height = 'Must be greater than 0';
	}
}

export function getDefaultFreightWizardData(): FreightBookingWizardData {
	return new FreightBookingWizardData({
		type: 'freight',
		freightTypeId: '',
		freightCategoryId: '',
		quantity: null,
		weight: null,
		width: null,
		height: null,
		length: null,
		handlingIds: [],
		description: '',
		dynamicFieldValues: {},
		departingTripOptions: [],
		returningTripOptions: [],
	});
}

export function freightBookingWizardDataHasChanged(
	newInnerWizardData: FreightBookingWizardData,
	oldInnerWizardData: FreightBookingWizardData,
): boolean {
	if (newInnerWizardData.freightTypeId !== oldInnerWizardData.freightTypeId) {
		return true;
	}
	if (newInnerWizardData.freightCategoryId !== oldInnerWizardData.freightCategoryId) {
		return true;
	}
	if (newInnerWizardData.handlingIds !== oldInnerWizardData.handlingIds) {
		return true;
	}
	if (newInnerWizardData.quantity !== oldInnerWizardData.quantity) {
		return true;
	}
	if (newInnerWizardData.weight !== oldInnerWizardData.weight) {
		return true;
	}
	if (newInnerWizardData.width !== oldInnerWizardData.width) {
		return true;
	}
	if (newInnerWizardData.height !== oldInnerWizardData.height) {
		return true;
	}
	if (newInnerWizardData.length !== oldInnerWizardData.length) {
		return true;
	}
	if (newInnerWizardData.description !== oldInnerWizardData.description) {
		return true;
	}

	// Receiver details
	if (appConfigStore.freightReceiverDetailsEnabled) {
		if (newInnerWizardData.receiverFirstName !== oldInnerWizardData.receiverFirstName) {
			return true;
		}
		if (newInnerWizardData.receiverLastName !== oldInnerWizardData.receiverLastName) {
			return true;
		}
		if (newInnerWizardData.receiverPhone !== oldInnerWizardData.receiverPhone) {
			return true;
		}
		if (newInnerWizardData.receiverEmail !== oldInnerWizardData.receiverEmail) {
			return true;
		}
		if (newInnerWizardData.receiverCompanyName !== oldInnerWizardData.receiverCompanyName) {
			return true;
		}
	}

	// Notify details
	if (appConfigStore.freightNotifyDetailsEnabled) {
		if (newInnerWizardData.notifyFirstName !== oldInnerWizardData.notifyFirstName) {
			return true;
		}
		if (newInnerWizardData.notifyLastName !== oldInnerWizardData.notifyLastName) {
			return true;
		}
		if (newInnerWizardData.notifyPhone !== oldInnerWizardData.notifyPhone) {
			return true;
		}
		if (newInnerWizardData.notifyEmail !== oldInnerWizardData.notifyEmail) {
			return true;
		}
		if (newInnerWizardData.notifyCompanyName !== oldInnerWizardData.notifyCompanyName) {
			return true;
		}
	}

	return false;
}

export function freightBookingWizardIsCompletedForPriceCalculations(
	innerWizardData: FreightBookingWizardData,
): boolean {
	const result =
		stringNotEmpty(innerWizardData.freightTypeId) &&
		stringNotEmpty(innerWizardData.freightCategoryId) &&
		isNotNullOrUndefined(innerWizardData.quantity) &&
		innerWizardData.quantity > 0 &&
		isNotNullOrUndefined(innerWizardData.weight) &&
		innerWizardData.weight > 0 &&
		isNotNullOrUndefined(innerWizardData.height) &&
		innerWizardData.height > 0 &&
		isNotNullOrUndefined(innerWizardData.length) &&
		innerWizardData.length > 0 &&
		isNotNullOrUndefined(innerWizardData.width) &&
		innerWizardData.width > 0;
	return result;
}
